/**
 * The monthly report card of a Saidiseire site page (item D,
 * docs/saidiseire-kiirus-turve-raport.md): the months with their reports,
 * download, send to the client, rebuild, and the current month's interim
 * report.
 *
 * The report of the previous month is built on its own on the first of the
 * month and the desk is told; nothing reaches the client until a person
 * opens it here and presses "Saada kliendile" (decision 5). Once a report
 * went out, that button steps back to a plain "Saada uuesti". A report that was
 * sent is rebuilt only after a confirmation: the copy the client received
 * stays in their mail thread, and the row forgets that it was sent.
 *
 * Reading is `view:crm`; building and sending answer to
 * `manage:crm.monitoring`. A site whose package leaves the report out says so
 * instead of offering buttons the server would refuse (a site with no package
 * at all is told the report needs one); its older reports stay downloadable.
 * Rebuilding a report that was sent, from its row or from the header's
 * interim button, always goes through the confirmation.
 */
import { useState } from "react";
import { Download, FileText, Loader2, RefreshCw, Send } from "lucide-react";
import { useI18n } from "../../../../i18n/I18nProvider";
import { monitorErrorKey } from "../../../../lib/monitoring";
import {
  buildMonthReport,
  currentReport,
  dayLabel,
  downloadMonthReport,
  missingPreviousMonth,
  monthLabel,
  refusalMeansCardIsBehind,
  rowState,
  rowTone,
  sendButton,
  sentOnlyToTestInbox,
  type MonthReportRowView,
  type MonthlyCardResponse,
  type MonthlySendResponse,
} from "../../../../lib/monitoringMonthly";
import { Btn, CardHead, StatusPill, fmtDateTime, useToast } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import type { T } from "../words";
import { SendDialog } from "./SendDialog";
import { useMonthly } from "./useMonthly";

export interface MonthlyCardProps {
  monitorId: string;
  /** The site's package includes the monthly report (`MonitorPolicyView.monthlyReport`). */
  allowed: boolean;
  /** The site has a package at all (`MonitorPolicyView.source === "plan"`). */
  hasPackage: boolean;
  /** `manage:crm.monitoring`: may build and send reports. */
  canManage: boolean;
  t: T;
}

/** The grey line under a row's month: when it was built, sent, or why it failed. */
function rowLine(t: T, row: MonthReportRowView): string {
  const state = rowState(row);
  if (state === "running") return t("monitoring.monthly.line.running");
  if (state === "failed") return t(row.error === "stalled" ? "monitoring.monthly.line.stalled" : "monitoring.monthly.line.failed");
  if (state === "sentTest") {
    return t("monitoring.monthly.line.sentTest", { when: fmtDateTime(row.sentAt), to: row.sentRedirectedFrom.join(", ") });
  }
  if (state === "sent") return t("monitoring.monthly.line.sent", { when: fmtDateTime(row.sentAt), to: row.sentTo.join(", ") });
  return t("monitoring.monthly.line.built", { when: fmtDateTime(row.generatedAt) });
}

function ReportRow({
  row,
  canAct,
  canSend,
  busy,
  locale,
  t,
  onDownload,
  onSend,
  onRebuild,
}: {
  row: MonthReportRowView;
  canAct: boolean;
  canSend: boolean;
  busy: boolean;
  locale: string;
  t: T;
  onDownload: () => void;
  onSend: () => void;
  onRebuild: () => void;
}) {
  const state = rowState(row);
  const ready = row.status === "ready";
  const send = sendButton(row);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[var(--crm-hair)] py-3 first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold">
          {monthLabel(row.month, locale)}
          {row.partial ? (
            <span className="crm-chip">
              {row.from && row.to
                ? t("monitoring.monthly.partialRange", { from: dayLabel(row.from), to: dayLabel(row.to) })
                : t("monitoring.monthly.partial")}
            </span>
          ) : null}
        </p>
        <p className={state === "failed" ? "text-[12.5px] text-[var(--color-destructive)]" : "crm-sub text-[12.5px]"}>
          {rowLine(t, row)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={rowTone(state)}>{t(`monitoring.monthly.state.${state}`)}</StatusPill>
        {ready ? (
          <Btn small onClick={onDownload} disabled={busy}>
            <Download className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.monthly.download")}
          </Btn>
        ) : null}
        {ready && canSend ? (
          <Btn small tone={send.tone} onClick={onSend} disabled={busy}>
            <Send className="size-[15px]" strokeWidth={1.8} />
            {t(send.key)}
          </Btn>
        ) : null}
        {canAct && state !== "running" ? (
          <Btn small onClick={onRebuild} disabled={busy}>
            <RefreshCw className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.monthly.rebuild")}
          </Btn>
        ) : null}
      </div>
    </li>
  );
}

export function MonthlyCard({ monitorId, allowed, hasPackage, canManage, t }: MonthlyCardProps) {
  const { locale } = useI18n();
  const { data, failed, reload } = useMonthly(monitorId);
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [sending, setSending] = useState<MonthReportRowView | null>(null);
  const [confirming, setConfirming] = useState<MonthReportRowView | null>(null);

  const title = (
    <span className="inline-flex items-center gap-2">
      <FileText className="size-4 text-muted-foreground" strokeWidth={1.8} />
      {t("monitoring.monthly.title")}
    </span>
  );

  async function build(month: string, force = false) {
    setBusy(month);
    try {
      const res = await buildMonthReport(monitorId, month, force);
      toast(t(res.report.status === "ready" ? "monitoring.monthly.toast.built" : "monitoring.monthly.toast.buildFailed"));
      void reload();
    } catch (err) {
      toast(t(`monitoring.error.${monitorErrorKey(err)}`));
      // A refusal often means the card is behind (somebody else sent or is building the report):
      // read it again, so the next press offers what the server will take.
      void reload();
    } finally {
      setBusy(null);
    }
  }

  /** A send refused because the row changed under the dialog: say why, close it, read the card again. */
  function sendRefused(message: string) {
    toast(message);
    setSending(null);
    void reload();
  }

  async function download(month: string) {
    setBusy(month);
    try {
      await downloadMonthReport(monitorId, month);
    } catch {
      toast(t("monitoring.monthly.toast.downloadFailed"));
    } finally {
      setBusy(null);
    }
  }

  function sent(res: MonthlySendResponse) {
    toast(t(res.redirectedFrom ? "monitoring.monthly.toast.sentTest" : "monitoring.monthly.toast.sent"));
    void reload();
  }

  if (!data) {
    return (
      <section className="crm-card mb-4">
        <CardHead title={title} />
        <div className="flex justify-center px-[26px] pb-6 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.monthly.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </section>
    );
  }

  const card: MonthlyCardResponse = data;
  const canAct = canManage && allowed;
  const canSend = canManage && card.mailbox;
  const missing = canAct ? missingPreviousMonth(card) : null;
  const current = currentReport(card);

  const actions = !allowed ? (
    <StatusPill tone="mut">{t(hasPackage ? "monitoring.monthly.notInPlanPill" : "monitoring.monthly.noPackagePill")}</StatusPill>
  ) : canAct ? (
    <>
      {missing ? (
        <Btn small disabled={busy !== null} onClick={() => void build(missing)}>
          {busy === missing ? <Loader2 className="size-[15px] animate-spin" /> : <FileText className="size-[15px]" strokeWidth={1.8} />}
          {t("monitoring.monthly.buildMonth", { month: monthLabel(missing, locale) })}
        </Btn>
      ) : null}
      {/* A sent interim report is rebuilt only through the confirmation, as from its row. */}
      <Btn
        small
        disabled={busy !== null || current?.status === "running"}
        onClick={() => (current?.sentAt ? setConfirming(current) : void build(card.currentMonth))}
      >
        {busy === card.currentMonth ? <Loader2 className="size-[15px] animate-spin" /> : <FileText className="size-[15px]" strokeWidth={1.8} />}
        {t("monitoring.monthly.buildCurrent")}
      </Btn>
    </>
  ) : null;

  const kept = card.reports.length > 0;
  const body = !allowed
    ? t(
        hasPackage
          ? kept
            ? "monitoring.monthly.notInPlanKept"
            : "monitoring.monthly.notInPlan"
          : kept
            ? "monitoring.monthly.noPackageKept"
            : "monitoring.monthly.noPackage",
      )
    : card.reports.length === 0
      ? t("monitoring.monthly.empty")
      : null;

  return (
    <section className="crm-card mb-4">
      <CardHead title={title} sub={t("monitoring.monthly.sub")} actions={actions} />
      {body ? <p className="crm-sub px-[26px] pb-4 text-[13px]">{body}</p> : null}
      {canManage && !card.mailbox && card.reports.length > 0 ? (
        <p className="px-[26px] pb-3 text-[13px] text-[var(--color-warning)]">{t("monitoring.monthly.noMailbox")}</p>
      ) : null}
      {card.reports.length > 0 ? (
        <ul className="px-[26px] pb-3">
          {card.reports.map((row) => (
            <ReportRow
              key={row.month}
              row={row}
              canAct={canAct}
              canSend={canSend}
              busy={busy === row.month}
              locale={locale}
              t={t}
              onDownload={() => void download(row.month)}
              onSend={() => setSending(row)}
              onRebuild={() => (row.sentAt ? setConfirming(row) : void build(row.month))}
            />
          ))}
        </ul>
      ) : null}

      {sending ? (
        <SendDialog
          monitorId={monitorId}
          report={sending}
          recipients={card.recipients}
          locale={locale}
          t={t}
          onClose={() => setSending(null)}
          onSent={sent}
          onRefused={sendRefused}
        />
      ) : null}
      {confirming ? (
        <ConfirmModal
          title={t("monitoring.monthly.rebuildTitle", { month: monthLabel(confirming.month, locale) })}
          body={t(sentOnlyToTestInbox(confirming) ? "monitoring.monthly.rebuildSentTestBody" : "monitoring.monthly.rebuildSentBody", {
            when: fmtDateTime(confirming.sentAt),
          })}
          confirmLabel={t("monitoring.monthly.rebuild")}
          cancelLabel={t("common.cancel")}
          tone="dark"
          onConfirm={async () => {
            const month = confirming.month;
            setConfirming(null);
            await build(month, true);
          }}
          onClose={() => setConfirming(null)}
        />
      ) : null}
    </section>
  );
}
